import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ApplicationService from '../services/axios.service';
import { toast } from 'react-toastify';

const BulkUploadForecast = () => {
  const [file, setFile] = useState(null);
  const [history, setHistory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errorRecords, setErrorRecords] = useState([]); // New state for error details
  const navigate = useNavigate();
  const service = new ApplicationService();
  const fileInputRef = useRef(null);

  // Handle file input change
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  };

  // Handle drag over (for styling)
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle clear button click
  const handleClear = () => {
    setFile(null);
    // Reset the file input's value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle upload button click
  const handleUpload = async () => {
    if (!file) {
      toast.warning('Please select a file to upload!');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await service.importforecast(formData);
      const result = response.data;
      // Check if there are success records
      if (result.successRecords && result.successRecords.length > 0) {
        toast.success(result.message || "File processed successfully");
        // Update history with the file upload
        setHistory([...history, { fileName: file.name, date: new Date().toLocaleString() }]);

        if (result.errorRecords && result.errorRecords.length == 0) {
          navigate("/forecasting");
          setErrorRecords([]);
        }
      }

      if (result.errorRecords && result.errorRecords.length > 0) {
        toast.warning("Some records failed to process. Please check the error details.");
        setErrorRecords(result.errorRecords);
      } else {
        toast.warning("No records processed.");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.warning("Error uploading file!");
    } finally {
      setUploading(false);
      setFile(null);
      // Reset the file input's value after upload
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Download template functionality
  const handleDownloadTemplate = () => {
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = '/excel/sample_forecast_template.xlsx'; // Ensure the file is placed in the public folder with this name
    link.download = 'forecast_template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex justify-center items-center p-2 bg-gray-50">
      <div className="w-full bg-white shadow-lg rounded-lg p-4">
        <div className="flex justify-between mb-6">
          {/* Download Template Button */}
          <button
            onClick={handleDownloadTemplate}
            className="bg-[#287DD1] text-white py-2 px-6 rounded-md hover:bg-[#2858d1]"
          >
            <span className="text-white cursor-pointer">Download Template</span>
          </button>

          {/* Bulk Upload History */}
          <button
            onClick={() => navigate("/bulk-upload-history")}
            className="bg-[#287DD1] text-white py-2 px-6 rounded-md hover:bg-[#2858d1] cursor-pointer"
          >
            <span className="text-white">Bulk Upload History</span>
          </button>
        </div>

        <div className="mb-8">
          <label htmlFor="file-upload" className="block text-white font-medium mb-2">
            Upload Excel *
          </label>
          {/* <input
            type="file"
            id="file-upload"
            accept=".xls,.xlsx"
            onChange={handleFileChange}
            ref={fileInputRef}
            className="w-full border border-gray-300 rounded-md p-2 mb-4"
          />
          <p className="text-sm text-gray-500">Only .xls/.xlsx file is allowed.</p> */}

          {/* Drag-and-Drop Area */}
          <div
            className="flex items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current.click()} // Trigger file input click on drag zone click
          >
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <svg className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
              </svg>
              <p className="mb-2 text-sm text-gray-500 dark:text-gray-400"><span className="font-semibold">Click to upload</span> or drag and drop</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Only .xls/.xlsx file is allowed.</p>
            </div>
            {/* Hidden File Input */}
            <input
              id="dropzone-file"
              type="file"
              className="hidden"
              onChange={handleFileChange}
              ref={fileInputRef}
              accept=".xls,.xlsx"
            />
          </div>
          {file && (
            <p className="text-green-600 mt-2">File ready for upload: <strong>{file.name}</strong></p>
          )}
           {/* Instructions for file upload */}
          <p className="text-sm text-gray-500 mt-2">Only .xls/.xlsx file is allowed.</p>
        </div>

        <div className="flex justify-start gap-4">
          <button
            onClick={handleClear}
            disabled={!file || uploading}
            style={{ color: '#fff' }}
            className={`py-2 px-6 rounded-md ${file && !uploading ? 'bg-[#287DD1] hover:bg-[#287DD1]  cursor-pointer' : 'bg-gray-400 cursor-not-allowed'} text-[#fff]`}
          >
            Clear
          </button>

          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            style={{ color: '#fff' }}
            className={`py-2 px-6 rounded-md ${file && !uploading ? 'bg-[#107C41] hover:bg-[#107C41] cursor-pointer' : 'bg-gray-400 cursor-not-allowed'} text-white`}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>

        {/* Display errors above the instructions if any */}
        {errorRecords.length > 0 && (
          <div className="mt-8 bg-red-100 border border-red-400 text-red-700 p-4 rounded">
            <h3 className="font-medium text-lg mb-2">Upload Errors</h3>
            <ul className="list-disc pl-5">
              {errorRecords.map((err, idx) => (
                <li key={idx}>
                  Row {err.row}: {err.error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Instructions Section */}
        <div className="mt-8 text-gray-700">
          <h3 className="font-medium text-lg mb-2">Instructions for Bulk Upload</h3>
          <ul className="list-decimal pl-5 space-y-2 list-none">
            <li>Step 1: Download the Excel template for bulk upload from the 'Download Template' button.</li>
            <li>Step 2: Fill in the data and upload the appropriate Excel using the 'Upload' button.</li>
            <li>Step 3: Check the errors or success records in the 'Bulk Upload History'.</li>
            <li>Step 4: Correct the data to ensure there are no errors and repeat the process.</li>
            <li>Step 5: If the status is 'Success', then the process has been successfully finished.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadForecast;
